/** Spare parts catalogue with reorder suggestions. */
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/app/auth";
import { ItemDialog } from "@/features/inventory/ItemDialogs";
import { ProcurementPanel } from "@/features/inventory/ProcurementPanel";
import {
  type Item,
  type ItemFilters,
  type ReorderSuggestion,
  useCategories,
  useItemPage,
  useReorderSuggestions,
} from "@/features/inventory/queries";
import { ITEM_KINDS } from "@/features/admin/reference/queries";
import { DataTable } from "@/shared/components/data-table";
import { CompleteDataTable } from "@/shared/components/complete-data-table";
import { ItemName } from "@/shared/components/item-name";
import { downloadTableExport, TableExportButton } from "@/shared/components/table-export-button";
import { TableToolbar, type TableFilterDefinition } from "@/shared/components/table-toolbar";
import { bindTableFilters, tableSortFields } from "@/shared/lib/table-controls";
import { ErrorState, LoadingState, PageHeader } from "@/shared/components/page";
import { formatQuantity } from "@/shared/lib/format";
import { PAGE_SIZES, useListState } from "@/shared/lib/list-state";
import { Button } from "@/shared/ui/button";
import { Badge, Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/primitives";

/** Items tab. */
function ItemsTab(): ReactElement {
  const { t } = useTranslation();
  const list = useListState("/inventory", "sku");
  const { search, state } = list;
  const filters: ItemFilters = {
    q: search.q,
    below_min: search.below_min,
    kind: search.kind,
    category_id: search.category_id,
    include_inactive: search.include_inactive,
  };
  const page = useItemPage(filters, state);
  const categories = useCategories();
  const update = list.update;

  const field = bindTableFilters(filters, update);
  const definitions: TableFilterDefinition[] = [
    {
      ...field("kind", t("app.type")),
      kind: "select",
      prominent: true,
      options: ITEM_KINDS.map((value) => ({ value, label: t(`inventory.kinds.${value}`) })),
    },
    {
      ...field("category_id", t("inventory.category")),
      kind: "select",
      prominent: true,
      options: (categories.data ?? []).map((category) => ({
        value: category.id,
        label: category.name,
      })),
    },
    {
      ...field("below_min", t("inventory.belowMin")),
      kind: "boolean",
      active: !!filters.below_min,
    },
    {
      ...field("include_inactive", t("inventory.includeInactive")),
      kind: "boolean",
      active: !!filters.include_inactive,
    },
  ];

  const columns: ColumnDef<Item>[] = [
    {
      id: "sku",
      enableSorting: true,
      header: t("inventory.sku"),
      cell: (c) => <span className="font-mono text-xs">{c.row.original.sku}</span>,
    },
    {
      id: "name",
      enableSorting: true,
      header: t("app.name"),
      cell: (c) => <ItemName id={c.row.original.id} name={c.row.original.name} />,
    },
    {
      id: "kind",
      enableSorting: true,
      header: t("app.type"),
      cell: (c) => <Badge tone="outline">{c.row.original.kind}</Badge>,
    },
    { id: "uom", enableSorting: true, header: t("app.unit"), cell: (c) => c.row.original.uom },
    {
      id: "on_hand",
      enableSorting: true,
      header: t("inventory.onHand"),
      cell: (c) => (
        <span className="tabular-nums">{formatQuantity(c.row.original.total_on_hand)}</span>
      ),
    },
    {
      id: "standard_cost",
      enableSorting: true,
      header: t("inventory.unitCost"),
      cell: (c) => (
        <span className="tabular-nums">{formatQuantity(c.row.original.standard_cost)}</span>
      ),
    },
    {
      id: "active",
      header: t("app.status"),
      cell: (c) =>
        c.row.original.is_active ? (
          <Badge tone="success">{t("app.yes")}</Badge>
        ) : (
          <Badge>{t("app.no")}</Badge>
        ),
    },
  ];

  return (
    <div>
      <TableToolbar
        filters={definitions}
        search={search.q}
        searchPlaceholder={t("tablePilot.itemSearch")}
        searchDescription={t("tablePilot.itemSearchScope")}
        onSearchChange={(q) => update({ q })}
        sorting={state.sort}
        sortFields={tableSortFields(columns)}
        onSortingChange={list.setSort}
        onReset={() =>
          update({
            q: undefined,
            kind: undefined,
            category_id: undefined,
            below_min: undefined,
            include_inactive: undefined,
          })
        }
        actions={
          <TableExportButton
            onExport={(format) =>
              downloadTableExport(
                "/api/v1/inventory/items",
                "inventory-items",
                { ...filters, ordering: state.sort },
                format,
              )
            }
          />
        }
      />
      {page.query.isPending ? (
        <LoadingState />
      ) : page.query.isError ? (
        <ErrorState error={page.query.error} onRetry={() => void page.query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          data={page.items}
          minWidth={900}
          getRowId={(i) => i.id}
          rowHref={(i) => `/inventory/${i.id}`}
          sorting={state.sort}
          onSortingChange={list.setSort}
          isFetching={page.query.isFetching}
          pagination={{
            page: page.page,
            pages: page.pages,
            total: page.total,
            pageSize: page.pageSize,
            onPageChange: list.setPage,
            onPageSizeChange: list.setSize,
            sizes: PAGE_SIZES,
            extendedNavigation: true,
          }}
        />
      )}
    </div>
  );
}

/** Reorder suggestions tab. */
function SuggestionsTab(): ReactElement {
  const { t } = useTranslation();
  const q = useReorderSuggestions(true);
  const columns: ColumnDef<ReorderSuggestion>[] = [
    {
      id: "sku",
      header: t("inventory.sku"),
      cell: (c) => <span className="font-mono text-xs">{c.row.original.sku}</span>,
    },
    { id: "name", header: t("app.name"), cell: (c) => c.row.original.name },
    { id: "location", header: t("inventory.location"), cell: (c) => c.row.original.location_code },
    {
      id: "on_hand",
      header: t("inventory.onHand"),
      cell: (c) => <span className="tabular-nums">{formatQuantity(c.row.original.on_hand)}</span>,
    },
    {
      id: "min",
      header: "Min",
      cell: (c) => <span className="tabular-nums">{formatQuantity(c.row.original.min_qty)}</span>,
    },
    {
      id: "suggested",
      header: t("inventory.suggested"),
      cell: (c) => (
        <span className="font-medium tabular-nums">
          {formatQuantity(c.row.original.suggested_qty)}
        </span>
      ),
    },
  ];
  if (q.isPending) {
    return <LoadingState />;
  }
  if (q.isError) {
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  }
  return (
    <CompleteDataTable
      columns={columns}
      data={q.data}
      getSearchText={(suggestion) =>
        `${suggestion.sku} ${suggestion.name} ${suggestion.location_code}`
      }
      searchDescription={t("tablePilot.searchFields", {
        fields: `${t("inventory.sku")}, ${t("app.name")}, ${t("inventory.location")}`,
      })}
      sortFields={[
        { value: "sku", label: t("inventory.sku"), valueOf: (suggestion) => suggestion.sku },
        { value: "name", label: t("app.name"), valueOf: (suggestion) => suggestion.name },
      ]}
      minWidth={700}
      getRowId={(s) => `${s.item_id}:${s.location_id}`}
      rowHref={(s) => `/inventory/${s.item_id}`}
      emptyMessage={t("inventory.noSuggestions")}
      total={q.data.length}
    />
  );
}

/** Inventory page. */
export function InventoryPage(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const showSuggestions = can("inventory.reorder.view");
  const [createOpen, setCreateOpen] = useState(false);
  return (
    <div>
      <PageHeader
        title={t("inventory.title")}
        actions={
          <>
            {can("inventory.stock.view") ? (
              <Button
                variant="outline"
                onClick={() => void navigate({ to: "/warehouse", search: { tab: "stock" } })}
              >
                {t("warehouse.title")}
              </Button>
            ) : null}
            {can("inventory.item.manage") ? (
              <Button onClick={() => setCreateOpen(true)}>{t("inventory.newItem")}</Button>
            ) : null}
          </>
        }
      />
      {createOpen ? (
        <ItemDialog
          onClose={() => setCreateOpen(false)}
          onCreated={(item) =>
            void navigate({ to: "/inventory/$itemId", params: { itemId: item.id } })
          }
        />
      ) : null}
      <Tabs
        value={search.tab ?? "items"}
        onValueChange={(tab) => void navigate({ to: "/inventory", search: { ...search, tab } })}
      >
        <TabsList>
          <TabsTrigger value="items">{t("inventory.title")}</TabsTrigger>
          {can("procurement.request.view") ? (
            <TabsTrigger value="procurement">{t("inventory.procurement.title")}</TabsTrigger>
          ) : null}
          {showSuggestions ? (
            <TabsTrigger value="reorder">{t("inventory.suggestions")}</TabsTrigger>
          ) : null}
        </TabsList>
        <TabsContent value="items">
          <ItemsTab />
        </TabsContent>
        {can("procurement.request.view") ? (
          <TabsContent value="procurement">
            <ProcurementPanel />
          </TabsContent>
        ) : null}
        {showSuggestions ? (
          <TabsContent value="reorder">
            <SuggestionsTab />
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
}
